import { useState, useEffect } from "react";
import { useProjectStore } from "@/zapi-frontend/stores/project";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { useChapterStore } from "@/zapi-frontend/stores/chapter";
import { AppButton, Typography } from "@libs/zapi-ui/custom";
import { InfoIcon, ListIcon } from "@phosphor-icons/react";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { useProjectSidebarStore, sidebarOptions } from "@/zapi-frontend/stores/projectSidebarStore";
import { ChapterIcon } from "../SidebarIsland/ChapterIcon";
import { Tooltip, TooltipContent, TooltipTrigger } from "@libs/zapi-ui/shadcn";
import { useMediaQuery } from "@/zapi-frontend/shared/hooks/useMediaQuery";
import { DashboardHomeIcon } from "./_DashboardHomeIcon";

type TProjectHeaderProps = {
  chapterMemoPolling: {
    chapters: Array<{
      id: number;
      hasActiveMemo: boolean;
      chapterName: string;
    }>;
  };
};

export function ProjectHeader(props: TProjectHeaderProps) {
  const { chapterMemoPolling } = props;
  const { openSidebar, toggleHandler, closeHandler } = useProjectSidebarStore();
  const { selectedProject, isFetchProjectLoading } = useProjectStore();
  const { activeChapter, isOpenChapterSidebar, toggleChapterSidebar, viewMode } = useChapterStore();
  const { isLabs, organizationName } = useMeStore();

  const isLaptop = useMediaQuery("(min-width: 984px)");
  const isTouchDevice = useMediaQuery("(hover: none) and (pointer: coarse)");
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  const isLoading = isFetchProjectLoading;

  // Check if the active chapter is loading (only during initial generation, not re-generation)
  const chapterMemoStatus = chapterMemoPolling.chapters.find((c) => c.id === Number(activeChapter?.id));
  const hasActiveMemo = chapterMemoStatus?.hasActiveMemo ?? true;

  const isActiveChapterLoading = !hasActiveMemo || isFetchProjectLoading;

  const prereqsNames = (activeChapter?.projectTemplateChapter.prereqs || []).map(
    (slug) =>
      selectedProject?.projectChapters?.find((i) => i.projectTemplateChapter.slug === slug)?.projectTemplateChapter
        .name || slug
  );

  // Close AQ/IQ sidebar when switching to a chapter where they're not available
  const chapterSlug = activeChapter?.projectTemplateChapter?.slug;
  useEffect(() => {
    const isAppendixChapter = chapterSlug?.startsWith("appendix-");
    const isAqIqOpen = openSidebar === "analysis" || openSidebar === "intelligence-questions";

    if (isAppendixChapter && isAqIqOpen) {
      closeHandler();
    }
  }, [chapterSlug, openSidebar, closeHandler]);

  return (
    <div data-testid="project-header">
      <div className="bg-white h-14 min-h-14 px-4 py-2 flex justify-between items-center border-b border-innoveraZinc-100">
        <div className="flex items-center gap-4 justify-between w-full">
          <div className="text-sm flex items-center gap-2">
            <AppButton
              appearance="text"
              size="icon"
              startAdornment={<ListIcon />}
              className="hd:hidden"
              data-chapter-sidebar-toggle
              onClick={() => toggleChapterSidebar(!isOpenChapterSidebar)}
            />
            {(viewMode === "dashboard" || viewMode === "projectOverview") && <DashboardHomeIcon />}
            {!isLoading && activeChapter && viewMode !== "dashboard" && viewMode !== "projectOverview" && (
              <ChapterIcon
                validQuestionsCount={activeChapter.validQuestionsCount}
                totalQuestions={activeChapter.totalQuestions}
                chapterIcon={activeChapter.projectTemplateChapter.icon}
                testId={`sidebar-chapter-${activeChapter?.id}-icon`}
              />
            )}
            <div className="flex items-end gap-2">
              <Typography
                data-testid="project-title"
                variant="heading6"
                className="max-w-[17vw] font-medium text-ellipsis overflow-hidden whitespace-nowrap"
              >
                {viewMode === "dashboard"
                  ? "Dashboard"
                  : viewMode === "projectOverview"
                  ? selectedProject?.name
                  : activeChapter && !isLoading
                  ? activeChapter.projectTemplateChapter.name
                  : "Loading project..."}{" "}
              </Typography>
              {!isLoading && (
                <Tooltip open={isTooltipOpen} onOpenChange={setIsTooltipOpen}>
                  <TooltipTrigger
                    asChild
                    onTouchStart={(e) => {
                      if (isTouchDevice) {
                        e.preventDefault();
                        setIsTooltipOpen((prev) => !prev);
                      }
                    }}
                  >
                    <button type="button" className="cursor-pointer select-none touch-manipulation">
                      <InfoIcon className="size-4 text-innoveraZinc-500 mb-[5px]" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <div className="flex flex-col gap-1 max-w-[400px]">
                      {selectedProject?.projectTemplateName && (
                        <Typography variant="body2" className="w-full">
                          Template name: <strong>{selectedProject.projectTemplateName}</strong>
                        </Typography>
                      )}
                      {isLabs && selectedProject?.projectTemplateSlug && (
                        <>
                          {" "}
                          <Typography variant="body2" className="w-full">
                            Template id: <strong>{selectedProject.projectTemplateSlug}</strong>
                          </Typography>{" "}
                          {viewMode !== "dashboard" && viewMode !== "projectOverview" && activeChapter && (
                            <Typography variant="body2" className="w-full">
                              Chapter id: <strong>{activeChapter?.id}</strong>
                            </Typography>
                          )}
                          <Typography variant="body2" className="w-full">
                            Organization name: <strong>{organizationName}</strong>
                          </Typography>
                          {viewMode !== "dashboard" && viewMode !== "projectOverview" && prereqsNames.length > 0 && (
                            <Typography variant="body2" className="w-full">
                              Prereqs: <strong>{prereqsNames.join(", ")}</strong>
                            </Typography>
                          )}
                        </>
                      )}
                    </div>
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          </div>
          <div className={cn("flex items-center gap-1")}>
            {viewMode !== "dashboard" &&
              viewMode !== "projectOverview" &&
              sidebarOptions
                .filter((option) => !option.isLabs || isLabs)
                .filter((option) => {
                  if (option.value === "analysis" || option.value === "intelligence-questions") {
                    return !activeChapter?.projectTemplateChapter?.slug.startsWith("appendix-");
                  }
                  return true;
                })
                .map((option) => (
                  <AppButton
                    key={option.value}
                    data-testid={`sidebar-${option.value}-button`}
                    startAdornment={<option.icon />}
                    appearance="text"
                    className={cn(
                      "text-innoveraZinc-500 font-medium",
                      openSidebar === option.value && "bg-innoveraZinc-100"
                    )}
                    title={option.title}
                    onClick={() => toggleHandler(option.value)}
                    disabled={isActiveChapterLoading}
                  >
                    {isLaptop && option.label}
                  </AppButton>
                ))}
          </div>
        </div>
      </div>
      {!isLoading &&
        viewMode !== "dashboard" &&
        viewMode !== "projectOverview" &&
        activeChapter?.projectTemplateChapter?.isExecutiveSummary && (
          <div className="w-full h-[140px]">
            <img
              src="/project-background.webp"
              alt="Project Details Page Background"
              className="w-full h-full object-cover"
            />
          </div>
        )}
    </div>
  );
}
